/**
 * Story 049 — `latest-inspections` widget body.
 *
 * Reads `GET /api/lyse/v1/data-acquisitions?sort=createdAt:desc&pageSize=5`
 * per story §"Widget set". Each row is a `<ListItemButton>` linking to
 * `/analysis/:id`.
 *
 * UX brief §3.4 — compact list with hairline dividers, full-width
 * clickable rows, chevron affordance, empty + error states.
 */
import { useMemo, type ReactElement } from "react";
import {
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { Link as RouterLink } from "react-router-dom";
import { WidgetRetryPill } from "./WidgetRetryPill.js";
import { ChevronRightIcon } from "@aeriosiq/ui/icons";

interface LatestInspectionsItem {
  id: string;
  site?: string | null;
  siteName?: string | null;
  assetCode?: string | null;
  pilotName?: string | null;
  pilotUserName?: string | null;
  createdAt?: string | null;
  // Backend may return additional fields; we read only the few above.
  [key: string]: unknown;
}

interface LatestInspectionsResponse {
  items?: LatestInspectionsItem[];
  total?: number;
}

function relativeDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diffMs = Date.now() - then;
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${String(hours)} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${String(days)} d ago`;
  const months = Math.round(days / 30);
  return `${String(months)} mo ago`;
}

function rowPrimary(item: LatestInspectionsItem): string {
  return (
    item.siteName ?? item.site ?? item.assetCode ?? `Acquisition ${item.id}`
  );
}

function rowSecondary(item: LatestInspectionsItem): string {
  const when = relativeDate(item.createdAt);
  const who = item.pilotName ?? item.pilotUserName ?? "Unassigned";
  return when ? `${when} · ${who}` : who;
}

export function LatestInspections(): ReactElement {
  const api = useApi();
  const query = useQuery<LatestInspectionsResponse>({
    queryKey: ["dashboard", "latest-inspections"],
    queryFn: async () => {
      const response = await api.get<LatestInspectionsResponse>(
        "/data-acquisitions",
        { params: { sort: "createdAt:desc", pageSize: 5 } },
      );
      return response.data;
    },
    staleTime: 30_000,
    retry: false,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data?.items]);

  if (query.isPending) {
    return (
      <Stack spacing={0} sx={{ px: 1, py: 1 }}>
        {Array.from({ length: 5 }).map((_, idx) => (
          <Skeleton
            key={idx}
            variant="rectangular"
            height={48}
            sx={{ borderRadius: 0.5, my: 0.25 }}
          />
        ))}
      </Stack>
    );
  }

  if (query.isError) {
    return (
      <WidgetRetryPill
        message="Couldn't load Latest inspections."
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  if (items.length === 0) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 2,
          py: 3,
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          No inspections yet
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Inspections show up here as your team uploads them.
        </Typography>
        <Button
          component={RouterLink}
          to="/data-acquisitions"
          variant="text"
          size="small"
          sx={{ mt: 1 }}
        >
          Open inspections list →
        </Button>
      </Box>
    );
  }

  return (
    <List dense disablePadding>
      {items.map((item, idx) => (
        <Box key={item.id}>
          {idx > 0 ? <Divider component="li" /> : null}
          <ListItemButton
            component={RouterLink}
            to={`/analysis/${item.id}`}
            sx={{ py: 1, px: 1.5 }}
          >
            <ListItemText
              primary={rowPrimary(item)}
              secondary={rowSecondary(item)}
              slotProps={{
                primary: { variant: "body2", color: "text.primary" },
                secondary: { variant: "caption", color: "text.secondary" },
              }}
            />
            <ChevronRightIcon
              fontSize="small"
              aria-hidden
              sx={{ color: "text.secondary", flexShrink: 0 }}
            />
          </ListItemButton>
        </Box>
      ))}
    </List>
  );
}
